import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, PageHeader } from "@/components/StatusBits";
import { BriefingFields } from "@/components/BriefingFields";
import { ScriptBuilder } from "@/components/ScriptBuilder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const NEW = { name: "New script", kind: "ai", objective_type: "followup", tone: "professional", opening_line: "", body: "", special_instructions: "", callback_instructions: "", message_text: "", repeat_count: 2 };

const Voices = () => {
  const { status } = useApp();
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    if (status?.elevenlabs?.configured) api.get("/voices").then((r) => setVoices(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  const play = async (id) => {
    try {
      const { data } = await api.post("/voices/preview", { voice_id: id });
      new Audio(data.audio).play();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!status?.elevenlabs?.configured) return <p className="text-sm text-slate-500" data-testid="voices-not-configured">ElevenLabs: Not configured. Add an API key in Settings → Integrations to choose and preview voices.</p>;
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="voices-list">
      {voices.map((v) => (
        <div key={v.voice_id} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2">
          <div><p className="text-sm font-medium">{v.name}</p><p className="text-[11px] text-slate-500">{v.category}</p></div>
          <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`voice-preview-${v.voice_id}`} onClick={() => play(v.voice_id)}><Volume2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
};

export default function Scripts() {
  const { isAdmin } = useApp();
  const [data, setData] = useState({ scripts: [], merge_fields: [] });
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [builder, setBuilder] = useState(false);
  const load = useCallback(() => api.get("/scripts").then((r) => {
    setData(r.data);
    setSel((cur) => cur || r.data.scripts[0] || null);
  }), []);
  useEffect(() => {
    load().catch(() => {});
  }, [load]);
  const save = async () => {
    try {
      const { data: s } = sel.id ? await api.put(`/scripts/${sel.id}`, sel) : await api.post("/scripts", sel);
      setSel(s);
      toast.success("Script saved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm("Delete this script?")) return;
    await api.delete(`/scripts/${sel.id}`);
    setSel(null);
    load();
  };
  const doPreview = async () => {
    try {
      const { data: p } = await api.post("/scripts/render", { script_id: sel.id, briefing: {} });
      setPreview(p);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setSel({ ...sel, [k]: e.target.value });
  return (
    <div className="max-w-7xl p-6 space-y-6">
      <PageHeader title="Scripts & AI" sub="Reusable scripts with merge fields. The selected script and caller ID are passed into every call.">
        <Button variant="outline" data-testid="scripts-build-btn" onClick={() => setBuilder(true)}><Sparkles className="h-4 w-4 mr-1" />Build a script</Button>
        {isAdmin && <Button data-testid="script-new-btn" onClick={() => { setSel(NEW); setPreview(null); }}><Plus className="h-4 w-4 mr-1" />New script</Button>}
      </PageHeader>
      <ScriptBuilder open={builder} onOpenChange={setBuilder} onSaved={(s) => { setSel(s); load(); }} />
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-2 space-y-1">
          {data.scripts.map((s) => (
            <button key={s.id} data-testid={`script-item-${s.id}`} onClick={() => { setSel(s); setPreview(null); }}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${sel?.id === s.id ? "bg-zinc-900 text-white" : "hover:bg-slate-100"}`}>{s.name}</button>
          ))}
        </div>
        <div className="lg:col-span-9 bg-white border border-slate-200 rounded-lg p-5">
          {!sel ? <p className="text-sm text-slate-500">Select a script.</p> : (
            <div className="space-y-4" data-testid="script-editor">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Field label="Name"><Input data-testid="script-name-input" value={sel.name} onChange={set("name")} disabled={!isAdmin} /></Field></div>
                <Field label="Type">
                  <Select value={sel.kind || "ai"} onValueChange={(v) => setSel({ ...sel, kind: v })} disabled={!isAdmin}>
                    <SelectTrigger data-testid="script-kind-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="ai">AI phone call</SelectItem><SelectItem value="message">Repeating message</SelectItem></SelectContent>
                  </Select>
                </Field>
              </div>
              {sel.purpose && <p className="text-xs text-slate-500" data-testid="script-purpose"><b>For:</b> {sel.purpose}{sel.goal && <> · <b>Goal:</b> {sel.goal}</>}</p>}
              {sel.kind === "message" && (
                <div className="grid sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-3"><Field label="Recorded message" hint="Merge fields work here. Callers can press 1 to talk to someone or 9 to stop calls."><Textarea rows={5} data-testid="script-message-input" value={sel.message_text || ""} onChange={set("message_text")} disabled={!isAdmin} /></Field></div>
                  <p className="text-xs text-slate-500 self-end" data-testid="script-repeat-note">Repeats non-stop until the person hangs up.</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Field label="Script / talking points" hint={`Merge fields: ${data.merge_fields.map((m) => `{${m}}`).join(" ")}`}>
                    <Textarea rows={10} data-testid="script-body-input" value={sel.body} onChange={set("body")} disabled={!isAdmin} />
                  </Field>
                </div>
                <BriefingFields prefix="script" value={sel} onChange={(v) => setSel({ ...sel, ...v })} />
              </div>
              <div className="flex gap-2">
                {isAdmin && <Button data-testid="script-save-btn" onClick={save}>Save</Button>}
                {sel.id && <Button variant="outline" data-testid="script-preview-btn" onClick={doPreview}>Preview with sample contact</Button>}
                {isAdmin && sel.id && <Button variant="ghost" className="text-rose-600 ml-auto" data-testid="script-delete-btn" onClick={del}><Trash2 className="h-4 w-4" /></Button>}
              </div>
              {preview && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs space-y-2" data-testid="script-preview">
                  <p><b>Opening line:</b> {preview.opening_line}</p>
                  <pre className="whitespace-pre-wrap font-sans text-slate-700">{preview.system_prompt}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <h2 className="font-heading text-lg font-semibold">Voices</h2>
        <Voices />
      </div>
    </div>
  );
}
